import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from ".";
import { InlineCardState } from "../contents/underlines/inlineCardSlice";
import {
  WritingAlert,
  alertsAdapter,
} from "../contents/underlines/writingAlertsSlice";
import { AttachedEditor, editorAdapter } from "../editorsSlice";
import useIsAlertIgnored from "../useIsAlertIgnored";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useAppDispatch = () => {
  return useDispatch<AppDispatch>();
};
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Alert selectors

export function useAlerts(editorId: number): WritingAlert[] {
  return useAppSelector((state) => {
    return alertsAdapter
      .getSelectors()
      .selectAll(state.writingAlerts)
      .filter((a) => a.editorId === editorId);
  });
}

export function useNonIgnoredAlerts(
  editorId: number,
  editorElement: HTMLElement
): WritingAlert[] {
  const isAlertIgnored = useIsAlertIgnored(editorElement);
  return useAlerts(editorId).filter(
    (a) => a.editorId === editorId && !isAlertIgnored(a)
  );
}

export function useRenderableAlerts(
  editorId: number,
  editorElement: HTMLElement
): WritingAlert[] {
  return useNonIgnoredAlerts(editorId, editorElement).filter(
    (a) => a.ui.overlayPositions.length > 0
  );
}

export function useAlert(
  alertId: number | undefined
): WritingAlert | undefined {
  return useAppSelector((state) =>
    alertId === undefined
      ? undefined
      : alertsAdapter.getSelectors().selectById(state.writingAlerts, alertId)
  );
}

// Editor selectors

export function useAllEditors(): AttachedEditor[] {
  return useAppSelector((state) => {
    return editorAdapter.getSelectors().selectAll(state.editors);
  });
}

export function useEditor(editorId: number): AttachedEditor | undefined {
  return useAppSelector((state) => {
    return editorAdapter.getSelectors().selectById(state.editors, editorId);
  });
}

export function useActiveEditor(): AttachedEditor | undefined {
  return useAppSelector((state) => {
    return state.editors.active !== null
      ? editorAdapter
          .getSelectors()
          .selectById(state.editors, state.editors.active)
      : undefined;
  });
}

// Inline card selectors

export function useInlineCard(): InlineCardState {
  return useAppSelector((state) => state.inlineCard);
}
